/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  getListRoomStyle,
  updateProfile,
  getProvinces,
  getCities,
  getBankList,
} from '../../services';
import {
  formatMoney,
  getFileExtension,
  getBase64FromUrlV3,
  getFileNameAndExtension,
  urlToFile,
} from '../../helper/lib';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DocUpload from '../../organism/DocUpload/DocUpload';
import { IsAlert } from '../../atoms/IsAlert';
import { availableStatusOptions, categories } from '../../helper/consts';
import FadeLoader from 'react-spinners/FadeLoader';
import AttachmentPreview from '../../organism/AttachmentPreview';

const ProfileDesigner = ({ dataProfile, benefits, getUserData }) => {
  const navigate = useNavigate();
  const [selectedAvailableStatus, setSelectedAvailableStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState();
  const [styles, setStyles] = useState([]);
  const [selectedStylesId, setSelectedStylesId] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState();
  const [provinces, setProvinces] = useState([]);
  const [selectedProvinceId, setSelectedProvinceId] = useState();
  const [selectedWhProvinceId, setSelectedWhProvinceId] = useState();
  const [cities, setCities] = useState([]);
  const [whCities, setWhCities] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState();
  const [selectedWhCityId, setSelectedWhCityId] = useState();
  const [samplePreview, setSamplePreview] = useState([]);
  const [showUploadInput, setShowUploadInput] = useState(true);
  const [progress, setProgress] = useState(0);
  const [loadAttachment, setLoadAttachment] = useState(false);

  const selectedCategory = categories.find(
    (category) => category.value === selectedCategoryId
  );

  const selectedStyles = styles.filter((item) =>
    selectedStylesId.includes(item.value)
  );

  const selectedBank = banks.find((bank) => bank.value === selectedBankId);

  const selectedProvince = provinces.find(
    (province) => province.value === selectedProvinceId
  );

  const selectedWhProvince = provinces.find(
    (province) => province.value === selectedWhProvinceId
  );

  const selectedCity = cities.find((city) => city.value === selectedCityId);

  const selectedWhCity = whCities.find(
    (whCity) => whCity.value === selectedWhCityId
  );

  const customStyles = {
    availableStatus: {
      control: (provided, state) => ({
        ...provided,
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999,
      }),
    },
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is Required.'),
    email: Yup.string()
      .required('Please enter valid email address.')
      .email('Email is required.'),
    about_merchant: Yup.string().required('Bio merchant is required.'),
    experience: Yup.string().required('Experience is required.'),
    availableStatus: Yup.string().required('Status is required.'),
    domain: Yup.string()
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .matches(
        /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/,
        'Website URL is not valid.'
      ),
    instagram: Yup.string()
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .matches(
        /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/,
        'Instagram URL is not valid.'
      ),
    styleProject: Yup.array().required('Style is required.'),
    address: Yup.string().required('Address is required.'),
    region_id: Yup.number().required('Province is required.'),
    district_id: Yup.number().required('City is required.'),
    postal_code: Yup.string()
      .required('Postal code is required.')
      .matches(/^[0-9]+$/, 'Only numbers.'),
    phone: Yup.string()
      .required('Phone number is required.')
      .test(
        'no-repeated-digits',
        'The phone number must not consist of repeating digits.',
        (value) => {
          const uniqueDigits = new Set(value.replace(/\D/g, ''));
          return uniqueDigits.size > 1;
        }
      ),
    wh_address: Yup.string()
      .transform((value) => (value === '' ? null : value))
      .nullable(),
    wh_region_id: Yup.number()
      .transform((value) => (value ? value : null))
      .nullable(),
    wh_district_id: Yup.number()
      .transform((value) => (value ? value : null))
      .nullable(),
    wh_postal_code: Yup.string()
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .matches(/^[0-9]+$/, 'Only numbers.'),
    wh_phone: Yup.string().test(
      'no-repeated-digits',
      'The phone number must not consist of repeating digits.',
      (value) => {
        if (!value) return true;
        const uniqueDigits = new Set(value.replace(/\D/g, ''));
        return uniqueDigits.size > 1;
      }
    ),
    merchant_bank_id: Yup.number().required('Bank is required.'),
    bank_branch: Yup.string()
      .required('Bank branch is required.')
      .min(3, 'Minimum 3 characters.')
      .matches(
        /^[a-zA-z]+[a-zA-Z0-9 ]*$/,
        'Only a combination of letters, numbers, and spaces.'
      ),
    bank_account_number: Yup.string()
      .required('Bank account number is required.')
      .min(3, 'Minimum 3 digit.')
      .max(16, 'Maximum 16 digit.')
      .matches(/^[0-9]+$/, 'Only numbers.'),
    bank_account_name: Yup.string()
      .required('Bank account name is required.')
      .min(3, 'Minimum 3 characters.')
      .max(100, 'Maximum 100 characters.')
      .matches(
        /^[a-zA-z]+[a-zA-Z ]*$/,
        'The first character cannot be empty. Only a combination of letters and spaces is allowed.'
      ),
    pic_name: Yup.string()
      .required('PIC name is required.')
      .min(3, 'Minimum 3 characters.')
      .max(50, 'Maximum 50 characters.')
      .matches(
        /^[a-zA-z]+[a-zA-Z ]*$/,
        'The first character cannot be empty. Only a combination of letters and spaces is allowed.'
      ),
    pic_email: Yup.string()
      .required('PIC email is required.')
      .email('PIC email format is not valid.'),
    pic_phone: Yup.string()
      .required('PIC phone number is required.')
      .test(
        'no-repeated-digits',
        'The PIC phone number must not consist of repeating digits.',
        (value) => {
          const uniqueDigits = new Set(value.replace(/\D/g, ''));
          return uniqueDigits.size > 1;
        }
      ),
    company_name: Yup.string()
      .required('Company name is required.')
      .min(3, 'Minimum 3 characters.')
      .max(100, 'Maximum 100 characters.')
      .matches(
        /^[a-zA-z]+[a-zA-Z ]*$/,
        'The first character cannot be empty. Only a combination of letters and spaces is allowed.'
      ),
    company_director_name: Yup.string()
      .required('Company owner name is required.')
      .min(3, 'Minimum 3 characters.')
      .max(100, 'Maximum 100 characters.')
      .matches(
        /^[a-zA-z]+[a-zA-Z ]*$/,
        'The first character cannot be empty. Only a combination of letters and spaces is allowed.'
      ),
    category: Yup.string().required('Category is required.'),
    docs: Yup.mixed().required('CV/Portofolio is required.'),
    cp_description: Yup.string().required(
      'Custom project description is required.'
    ),
    cp_price_per_m: Yup.string().required('Custom project price is required.'),
    benefits: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().min(5, 'Minimum 5 characters'),
      })
    ),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
  };

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
    clearErrors,
    unregister,
    trigger,
  } = useForm(formOptions);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'benefits',
  });

  const isInitialRender = useRef(true);

  const appendBenefit = () => {
    append({
      name: '',
    });

    if (errors.benefits?.type === 'min') {
      clearErrors('benefits'); // always clear errors when there is add action.
    }
  };

  const handleRemoveBenefit = (index) => {
    remove(index);

    if (fields.length === 1) {
      setValue('benefits', []);
    }
  };

  useEffect(() => {
    if (!fields.length && !isInitialRender.current) {
      trigger('questions');
    }

    if (isInitialRender.current) {
      isInitialRender.current = false;
    }
  }, [fields, register, setValue, unregister, trigger]);

  const getStyleLists = async () => {
    try {
      const response = await getListRoomStyle();
      if (response.message === 'Success' && response.data.length > 0) {
        const styleList = response.data.map((item) => {
          return { value: item.id, label: item.name };
        });
        setStyles(styleList);
      }
    } catch (error) {
      console.log(error.response.data.message);
    }
  };

  const getProvinceData = async () => {
    try {
      const response = await getProvinces();
      if (response.message === 'Success' && response.data.length > 0) {
        const province = response.data.map((item) => {
          return { value: item.id, label: item.name };
        });
        setProvinces(province);
      }
    } catch (error) {
      console.log('error:', error.response.data.message);
    }
  };

  const getCitiesData = async (id, type = 'office') => {
    try {
      const response = await getCities(id);

      if (response.message === 'Success' && response.data.length > 0) {
        const cities = response.data.map((item) => {
          return { value: item.id, label: item.name };
        });

        if (type === 'warehouse') {
          setWhCities(cities);
          return;
        }

        setCities(cities);
      }
    } catch (error) {
      console.log('error:', error.response.data.message);
    }
  };

  const getBankData = async () => {
    try {
      const response = await getBankList();
      if (response.message === 'Success' && response.data.length > 0) {
        const bank = response.data.map((item) => {
          return { value: item.id, label: item.name };
        });
        setBanks(bank);
      }
    } catch (error) {
      console.log('error:', error.response.data.message);
    }
  };

  const handleChangeBank = (e) => {
    setSelectedBankId(e.value);
    setValue('merchant_bank_id', e.value);
  };

  const handleChangeStyle = (selectedOption) => {
    setSelectedStylesId(selectedOption.map((item) => item.value));
    if (selectedOption.length > 0) {
      setValue(
        'styleProject',
        selectedOption.map((item) => item.value)
      );
      return;
    }

    setValue('styleProject');
  };

  const handleChangeAvailableStatus = (e) => {
    setSelectedAvailableStatus(e);
    setValue('availableStatus', e.value);
  };

  const handleChangeCategory = (e) => {
    setSelectedCategoryId(e.value);
    setValue('category', e.value);
  };

  const handleChangeProvince = (e) => {
    setSelectedProvinceId(e.value);
    setValue('region_id', e.value);
    getCitiesData(e.value);
  };

  const handleChangeCity = (e) => {
    setSelectedCityId(e.value);
    setValue('district_id', e.value);
  };

  const handleChangeWarehouseProvince = (e) => {
    setSelectedWhProvinceId(e.value);
    setValue('wh_region_id', e.value);
    getCitiesData(e.value, 'warehouse');
  };

  const handleChangeWarehouseCity = (e) => {
    setSelectedWhCityId(e.value);
    setValue('wh_district_id', e.value);
  };

  const loadDoc = async (data) => {
    const previewData = [];
    setLoadAttachment(true);

    for (let i = 0; i < data.length; i++) {
      let el = data[i];
      if (el.full_doc_url !== '') {
        const base64File = await getBase64FromUrlV3(el.full_doc_url);
        if (base64File !== '' && base64File !== undefined) {
          //console.log(getFileNameAndExtension(el.full_doc_url));
          const converURLtoFile = await urlToFile(
            el.full_doc_url,
            getFileNameAndExtension(el.full_doc_url)?.name,
            'application/pdf'
          );

          previewData.push({
            name: `${getFileNameAndExtension(el.full_doc_url)?.name}.${
              getFileNameAndExtension(el.full_doc_url)?.type
            }`,
            size: Math.round(((converURLtoFile.size / 1000000) * 100) / 100),
            preview: el.full_doc_url,
            file: base64File,
          });

          setValue('docs', converURLtoFile);
        }
      }
    }

    setSamplePreview(previewData);
    setLoadAttachment(false);
  };

  const uploadFileWithProgress = (file) => {
    return new Promise((resolve) => {
      const total = file.size;
      let uploaded = 0;

      const interval = setInterval(() => {
        uploaded += total * 0.1; // Simulate 10% upload progress per tick
        const progress = Math.min(Math.round((uploaded / total) * 100), 100);
        setProgress(progress);

        if (progress >= 100) {
          clearInterval(interval);
          resolve();
        }
      }, 200);
    });
  };

  const clearImageSelected = (index) => {
    const filtered = samplePreview.filter((_, i) => {
      return i !== index;
    });
    setSamplePreview(filtered);
    setProgress(0);
  };

  const handleUpload = async (files) => {
    if (samplePreview.length + files.length > 1) {
      IsAlert.fire({
        title: 'Error',
        text: 'Maximum Upload 1 Files',
        icon: 'error',
      });

      return;
    }
    files.forEach(async (file) => {
      const fileExtension = await getFileExtension(file.name);
      const fsize = Math.round((file.size / 1000000) * 100) / 100;
      if (!['pdf'].includes(fileExtension.toLowerCase())) {
        // Alert
        IsAlert.fire({
          title: 'Error',
          text: 'Please upload file with the following extension only: pdf',
          icon: 'error',
        });

        return;
      }

      if (fsize > 40.0) {
        IsAlert.fire({
          title: 'Error',
          text: 'Please upload doc with a size of 40 MB or less per file',
          icon: 'error',
        });
        return;
      }

      const previewImage = URL.createObjectURL(file);

      try {
        await uploadFileWithProgress(file);
        setSamplePreview((prev) => [
          ...prev,
          {
            name: file.name,
            size: fsize,
            preview: previewImage,
            file: file,
            extension: fileExtension,
          },
        ]);

        setValue('docs', file);
        clearErrors('docs');

        return;
      } catch (error) {
        IsAlert.fire({
          title: 'Error',
          text: 'An error occurred during the upload',
          icon: 'error',
        });
      }
    });
  };

  const onSubmitProfile = async (data, error) => {
    error.preventDefault();

    const loadingToast = toast.loading('Updating profile...');
    try {
      setErrorMessage(null);
      setIsSubmitting(true);

      const formData = new FormData();

      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('about_merchant', data.about_merchant);
      formData.append('experience', data.experience);
      formData.append('availableStatus', data.availableStatus);
      formData.append('domain', data.domain ?? '');
      formData.append('instagram', data.instagram ?? '');
      formData.append('styleProject', JSON.stringify(data.styleProject));
      formData.append('address', data.address);
      formData.append('region_id', data.region_id);
      formData.append('district_id', data.district_id);
      formData.append('postal_code', data.postal_code);
      formData.append('phone', data.phone);
      formData.append('wh_address', data.wh_address ?? '');
      formData.append(
        'wh_region_id',
        data.wh_region_id ? data.wh_region_id : ''
      );
      formData.append(
        'wh_district_id',
        data.wh_district_id ? data.wh_district_id : ''
      );
      formData.append('wh_postal_code', data.wh_postal_code ?? '');
      formData.append('wh_phone', data.wh_phone ?? '');
      formData.append('pic_name', data.pic_name);
      formData.append('pic_phone', data.pic_phone);
      formData.append('pic_email', data.pic_email);
      formData.append('merchant_bank_id', data.merchant_bank_id);
      formData.append('bank_branch', data.bank_branch);
      formData.append('bank_account_number', data.bank_account_number);
      formData.append('bank_account_name', data.bank_account_name);
      formData.append('company_name', data.company_name);
      formData.append('company_director_name', data.company_director_name);
      formData.append('category', data.category);
      formData.append('docs', data.docs);
      formData.append('use_custom_project', true);
      formData.append('cp_description', data.cp_description);
      formData.append(
        'cp_price_per_m',
        parseInt(String(data.cp_price_per_m).replaceAll('.', ''))
      );
      formData.append('benefits', JSON.stringify(data.benefits));

      const response = await updateProfile(formData, (progress) => {
        console.log(`Upload Progress: ${progress}%`);
      });

      if (response.message === 'Success') {
        getUserData();
        toast.update(loadingToast, {
          render: 'Profile updated',
          type: 'success',
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
          pauseOnHover: true,
        });

        navigate('/');
      }
    } catch (error) {
      setErrorMessage(error.response.data.message);
      toast.update(loadingToast, {
        render: error.response.data.message,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: true,
      });
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    getStyleLists();
    getProvinceData();
    getBankData();
  }, []);

  useEffect(() => {
    if (dataProfile) {
      // console.log(dataProfile);
      getFileNameAndExtension(dataProfile?.docs[0].full_doc_url);
      setValue('name', dataProfile?.name);
      setValue('company_director_name', dataProfile?.company_director_name);
      setValue('company_name', dataProfile?.company_name);
      setValue('category', dataProfile?.category);
      setSelectedCategoryId(dataProfile?.category);
      setValue('availableStatus', dataProfile?.availableStatus);
      setSelectedAvailableStatus({
        value: dataProfile?.availableStatus,
        label: dataProfile?.availableStatus,
      });
      setValue('email', dataProfile?.email);
      setValue('about_merchant', dataProfile?.about_merchant);
      setValue('domain', dataProfile?.domain ?? '');
      setValue('instagram', dataProfile?.instagram);

      setSelectedStylesId(
        dataProfile?.styleProject ? JSON.parse(dataProfile?.styleProject) : []
      );
      setValue('region_id', dataProfile?.region.id);
      setSelectedProvinceId(dataProfile?.region.id);
      getCitiesData(dataProfile?.region.id);
      setValue('district_id', dataProfile?.district.id);
      setSelectedCityId(dataProfile?.district.id);
      setValue('postal_code', dataProfile?.postal_code);
      setValue('phone', dataProfile?.phone);
      setValue('address', dataProfile?.address);
      setValue('wh_region_id', dataProfile?.wh_region_id);
      setSelectedWhProvinceId(dataProfile?.wh_region_id);
      if (dataProfile?.wh_region_id) {
        getCitiesData(dataProfile?.wh_region_id, 'warehouse');
      }
      setValue('wh_district_id', dataProfile?.wh_district_id);
      setSelectedWhCityId(dataProfile?.wh_district_id);
      setValue('wh_postal_code', dataProfile?.wh_postal_code);
      setValue('wh_phone', dataProfile?.wh_phone);
      setValue('wh_address', dataProfile?.wh_address);
      setValue('merchant_bank_id', dataProfile?.merchant_bank?.id);
      setSelectedBankId(dataProfile?.merchant_bank?.id);
      setValue('bank_branch', dataProfile?.bank_branch);
      setValue('bank_account_number', dataProfile?.bank_account_number);
      setValue('bank_account_name', dataProfile?.bank_account_name);
      setValue('pic_name', dataProfile?.pic_name);
      setValue('pic_phone', dataProfile?.pic_phone);
      setValue('pic_email', dataProfile?.pic_email);
      loadDoc(dataProfile?.docs);
      setValue('experience', dataProfile?.experience ?? '');
      setValue('use_custom_project', dataProfile?.use_custom_project);
      setValue('cp_description', dataProfile?.cp_description ?? '');
      setValue('cp_price_per_m', formatMoney(dataProfile?.cp_price_per_m, ''));
      if (dataProfile?.styleProject) {
        if (JSON.parse(dataProfile?.styleProject).length > 0) {
          setValue('styleProject', JSON.parse(dataProfile?.styleProject));
          return;
        }

        setValue('styleProject');
      }
    }
  }, [dataProfile]);

  useEffect(() => {
    if (benefits.length > 0) {
      setValue(
        'benefits',
        benefits.map((benefit) => ({ name: benefit?.name }))
      );
      return;
    }
    setValue('benefits', []);
  }, [benefits]);

  useEffect(() => {
    // show hide box image upload
    if (samplePreview.length >= 1) {
      setShowUploadInput(false);
    } else {
      if (showUploadInput) {
        return;
      }
      setShowUploadInput(true);
    }
  }, [samplePreview]);

  return (
    <form onSubmit={handleSubmit(onSubmitProfile)}>
      <div className="fs-5 fw-bold mb-4">
        Profile Information
        <i className="fa fas-pencil"></i>
      </div>

      {errorMessage !== null && (
        <div className="alert alert-danger alert-dismissible fade show">
          {errorMessage}
          <button
            type="button"
            className="close"
            onClick={() => setErrorMessage(null)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Username<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('name')}
            placeholder="Input name"
            disabled
          />

          {errors.name ? (
            <span className="text-danger">{errors.name.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            Email<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('email')}
            placeholder="Input email address"
            disabled
          />

          {errors.email ? (
            <span className="text-danger">{errors.email.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Company Legal Name<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('company_name')}
            placeholder="Input company name"
          />

          {errors.company_name ? (
            <span className="text-danger">{errors.company_name.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            Company Director / Owner Name
            <div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('company_director_name')}
            placeholder="Input company director name/owner name"
          />

          {errors.company_director_name ? (
            <span className="text-danger">
              {errors.company_director_name.message}
            </span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Bio<div className="text-danger ps-1">*</div>
          </label>

          <textarea
            className="form-control"
            rows="3"
            {...register('about_merchant')}
            placeholder="Input biodata"
          ></textarea>

          {errors.about_merchant ? (
            <span className="text-danger">{errors.about_merchant.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            Experience (Years)<div className="text-danger ps-1">*</div>
          </label>

          <input
            type="number"
            className="form-control"
            {...register('experience')}
            placeholder="Input experience"
            min={0}
          />
          {errors.experience ? (
            <span className="text-danger">{errors.experience.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Available Status<div className="text-danger ps-1">*</div>
          </label>

          <Controller
            name="availableStatus"
            control={control}
            render={({ field }) => (
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={selectedAvailableStatus}
                placeholder="Select Status"
                options={availableStatusOptions}
                isSearchable={true}
                styles={customStyles.availableStatus}
                menuPortalTarget={document.body}
                onChange={handleChangeAvailableStatus}
              />
            )}
          />

          {errors.availableStatus ? (
            <span className="text-danger">
              {errors.availableStatus.message}
            </span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">Website (URL)</label>
          <input
            type="text"
            className="form-control"
            {...register('domain')}
            placeholder="e.g: https://www.google.com"
          />

          {errors.domain ? (
            <span className="text-danger">{errors.domain.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Instagram Profile (URL)
          </label>
          <input
            type="text"
            className="form-control"
            {...register('instagram')}
            placeholder="e.g: https://www.instagram.com/xxx"
          />

          {errors.instagram ? (
            <span className="text-danger">{errors.instagram.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            Category<div className="text-danger ps-1">*</div>
          </label>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={selectedCategory}
                placeholder="Select Category"
                options={categories}
                isSearchable={false}
                styles={customStyles.category}
                menuPortalTarget={document.body}
                onChange={handleChangeCategory}
              />
            )}
          />

          {errors.category ? (
            <span className="text-danger">{errors.category.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-4">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Style<div className="text-danger ps-1">*</div>
          </label>
          <Select
            isMulti
            options={styles}
            value={selectedStyles}
            onChange={handleChangeStyle}
            menuPlacement="auto"
            placeholder="Select styles"
          />
          {errors.styleProject ? (
            <span className="text-danger">{errors.styleProject.message}</span>
          ) : null}
        </div>
      </div>

      <div className="fs-5 fw-bold mb-4">Custom Project</div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <div className="mb-3" key="cp-1">
            <label className="form-label d-flex fw-semibold">
              Price (Per Meter)
              <div className="text-danger ps-1">*</div>
            </label>
            <input
              {...register('cp_price_per_m')}
              type="text"
              className="form-control"
              onKeyUp={(event) =>
                setValue(
                  'cp_price_per_m',
                  formatMoney(event.target.value.replaceAll('.', ''), '')
                )
              }
            />
            {errors.cp_price_per_m ? (
              <span className="text-danger">
                {errors.cp_price_per_m.message}
              </span>
            ) : null}
          </div>

          <div className="mb-3" key="cp-2">
            <label className="form-label d-flex fw-semibold">
              Description<div className="text-danger ps-1">*</div>
            </label>
            <textarea
              className="form-control"
              rows="3"
              {...register('cp_description')}
            ></textarea>
            {errors.cp_description ? (
              <span className="text-danger">
                {errors.cp_description.message}
              </span>
            ) : null}
          </div>

          <div className="mb-3" key="cp-3">
            <div className="d-flex flex-column mb-3">
              <div className="d-flex flex-row align-items-center justify-content-between">
                <label className="form-label d-flex fw-semibold m-0">
                  Benefits
                </label>

                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={appendBenefit}
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>

              {errors.benefits ? (
                <span className="text-danger">{errors.benefits.message}</span>
              ) : null}
            </div>
            {fields.map((benefit, benefitIndex) => (
              <div key={benefit.id} className="mb-3">
                <div className="d-flex flex-row">
                  <input
                    type="text"
                    className="form-control"
                    {...register(`benefits[${benefitIndex}].name`)}
                    control={control}
                  />
                  <div
                    className="btn-group ms-3"
                    role="group"
                    aria-label="benefit-action-button-group"
                  >
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      style={{ width: '45px' }}
                      onClick={() => {
                        handleRemoveBenefit(benefitIndex);
                        trigger();
                      }}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                {errors?.benefits?.[benefitIndex]?.name ? (
                  <span className="text-danger">
                    {errors?.benefits?.[benefitIndex]?.name?.message}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fs-5 fw-bold mt-5 mb-4">Office Location</div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Province<div className="text-danger ps-1">*</div>
          </label>
          <Controller
            name="region_id"
            control={control}
            render={({ field }) => (
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={selectedProvince}
                placeholder="Select Province"
                options={provinces}
                isSearchable={true}
                styles={customStyles.province}
                menuPortalTarget={document.body}
                onChange={handleChangeProvince}
              />
            )}
          />
          {errors.region_id ? (
            <span className="text-danger">{errors.region_id.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            City<div className="text-danger ps-1">*</div>
          </label>
          <Controller
            name="district_id"
            control={control}
            render={({ field }) => (
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={selectedCity}
                placeholder="Select City"
                options={cities}
                isSearchable={true}
                styles={customStyles.city}
                menuPortalTarget={document.body}
                onChange={handleChangeCity}
              />
            )}
          />
          {errors.district_id ? (
            <span className="text-danger">{errors.district_id.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Postal code<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('postal_code')}
            maxLength={5}
            placeholder="Input postal code"
          />
          {errors.postal_code ? (
            <span className="text-danger">{errors.postal_code.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            Phone Number<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('phone')}
            maxLength={15}
            placeholder="Input phone number"
          />
          {errors.phone ? (
            <span className="text-danger">{errors.phone.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Address<div className="text-danger ps-1">*</div>
          </label>
          <textarea
            className="form-control"
            rows="3"
            {...register('address')}
            placeholder="Input address"
          ></textarea>
          {errors.address ? (
            <span className="text-danger">{errors.address.message}</span>
          ) : null}
        </div>
      </div>

      <div className="fs-5 fw-bold mt-5 mb-4">Warehouse Location</div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">Province</label>
          <Controller
            name="wh_region_id"
            control={control}
            render={({ field }) => (
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={selectedWhProvince}
                placeholder="Select Province"
                options={provinces}
                isSearchable={true}
                styles={customStyles.province}
                menuPortalTarget={document.body}
                onChange={handleChangeWarehouseProvince}
              />
            )}
          />
          {errors.wh_region_id ? (
            <span className="text-danger">{errors.wh_region_id.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">City</label>
          <Controller
            name="wh_district_id"
            control={control}
            render={({ field }) => (
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={selectedWhCity}
                placeholder="Select City"
                options={whCities}
                isSearchable={true}
                styles={customStyles.city}
                menuPortalTarget={document.body}
                onChange={handleChangeWarehouseCity}
              />
            )}
          />
          {errors.wh_district_id ? (
            <span className="text-danger">{errors.wh_district_id.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">Postal code</label>
          <input
            type="text"
            className="form-control"
            {...register('wh_postal_code')}
            maxLength={5}
            placeholder="Input postal code"
          />
          {errors.wh_postal_code ? (
            <span className="text-danger">{errors.wh_postal_code.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">Phone Number</label>
          <input
            type="text"
            className="form-control"
            {...register('wh_phone')}
            maxLength={15}
            placeholder="Input phone number"
          />
          {errors.wh_phone ? (
            <span className="text-danger">{errors.wh_phone.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">Address</label>
          <textarea
            className="form-control"
            rows="3"
            {...register('wh_address')}
            placeholder="Input address"
          ></textarea>
          {errors.wh_address ? (
            <span className="text-danger">{errors.wh_address.message}</span>
          ) : null}
        </div>
      </div>

      <div className="fs-5 fw-bold mt-5 mb-4">PIC Info</div>
      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            PIC Name<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('pic_name')}
            placeholder="Input PIC name"
          />

          {errors.pic_name ? (
            <span className="text-danger">{errors.pic_name.message}</span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            PIC Phone Number<div className="text-danger ps-1">*</div>
          </label>

          <input
            type="text"
            className="form-control"
            {...register('pic_phone')}
            maxLength={15}
            placeholder="Input PIC phone number"
          />

          {errors.pic_phone ? (
            <span className="text-danger">{errors.pic_phone.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            PIC Email<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('pic_email')}
            placeholder="Input PIC email"
          />

          {errors.pic_email ? (
            <span className="text-danger">{errors.pic_email.message}</span>
          ) : null}
        </div>
      </div>

      <div className="fs-5 fw-bold mt-5 mb-4">For Disbursement Purpose</div>
      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Bank Name<div className="text-danger ps-1">*</div>
          </label>
          <Controller
            name="merchant_bank_id"
            control={control}
            render={({ field }) => (
              <Select
                className="basic-single"
                classNamePrefix="select"
                value={selectedBank}
                placeholder="Select Bank"
                options={banks}
                isSearchable={true}
                menuPortalTarget={document.body}
                onChange={handleChangeBank}
                isDisabled
              />
            )}
          />
          {errors.merchant_bank_id ? (
            <span className="text-danger">
              {errors.merchant_bank_id.message}
            </span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            Bank Branch<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('bank_branch')}
            placeholder="Input bank branch name"
            disabled
          />

          {errors.bank_branch ? (
            <span className="text-danger">{errors.bank_branch.message}</span>
          ) : null}
        </div>
      </div>

      <div className="d-sm-flex mb-3">
        <div className="col-12 col-sm-6 mb-3 mb-sm-0 pe-sm-2">
          <label className="form-label d-flex fw-semibold">
            Bank Account Number<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('bank_account_number')}
            placeholder="Input bank account number"
            onChange={(e) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
            }}
            disabled
          />

          {errors.bank_account_number ? (
            <span className="text-danger">
              {errors.bank_account_number.message}
            </span>
          ) : null}
        </div>
        <div className="col-12 col-sm-6 ps-sm-2">
          <label className="form-label d-flex fw-semibold">
            Bank Account Name<div className="text-danger ps-1">*</div>
          </label>
          <input
            type="text"
            className="form-control"
            {...register('bank_account_name')}
            placeholder="Input bank account name"
            disabled
          />

          {errors.bank_account_name ? (
            <span className="text-danger">
              {errors.bank_account_name.message}
            </span>
          ) : null}
        </div>
      </div>

      <div className="fs-5 fw-bold mt-5 mb-4">Attachment</div>
      <div>
        <label className="form-label d-flex fw-semibold">
          CV/Portofolio Designer<div className="text-danger ps-1">*</div>
        </label>
        {loadAttachment ? null : (
          <>
            {showUploadInput ? (
              <DocUpload
                upload={handleUpload}
                samplePreviews={samplePreview}
                maxFile={1}
                maxSizePerFile={40}
              />
            ) : null}

            {errors.docs ? (
              <span className="text-danger mt-2">{errors.docs.message}</span>
            ) : null}
          </>
        )}
      </div>

      <div className="container m-0 p-0 w-100 mt-3">
        <div className="row m-0 mb-3 p-0 w-100">
          {samplePreview.map((image, index) => (
            <div className="col-12 col-sm-12 col-md-6 col-lg-4 m-0 p-0">
              <AttachmentPreview
                attachment={image}
                handleClear={clearImageSelected.bind(this, index)}
              />
            </div>
          ))}

          {loadAttachment ? (
            <div className="d-flex align-items-center p-0 m-0">
              <FadeLoader
                height={15}
                radius={2}
                width={5}
                speedMultiplier={1}
                loading={loadAttachment}
              />
              <h6 className="ms-3">Loading Attachment</h6>
            </div>
          ) : null}
        </div>
      </div>

      {progress === 0 ? null : (
        <div className="progress mt-3 mb-5" style={{ height: '20px' }}>
          <div
            className="progress-bar bg-dark-grey"
            role="progressbar"
            style={{ width: progress + '%' }}
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {progress}%
          </div>
        </div>
      )}

      <div className="d-flex justify-content-end mt-3">
        <div className="col-12 col-sm-4 ps-sm-2 d-flex justify-content-end">
          <button
            type="submit"
            className="btn m-0 w-100 fw-semibold btn-fill px-5"
            disabled={isSubmitting || loadAttachment}
          >
            {isSubmitting ? (
              <div
                className="spinner-border"
                role="status"
                style={{ width: '1.5rem', height: '1.5rem' }}
              >
                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ProfileDesigner;
